import React, { useEffect, useState } from 'react'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { theme } from '../../../../theme/theme'
import { Button, ButtonVariant } from '../../../common/Button'
import { IconChoices } from '../../../common/Icon'
import { TextInput } from '../../../common/TextInput'

interface RoomNameFieldProps {
  name: string
  canEdit: boolean
  isSaving: boolean
  onSave: (name: string) => void
}

export const RoomNameField = ({
  name,
  canEdit,
  isSaving,
  onSave,
}: RoomNameFieldProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(name)

  useEffect(() => {
    setEditing(false)
    setDraft(name)
  }, [name])

  const trimmed = draft.trim()
  const canSave = trimmed.length > 0 && trimmed !== name && !isSaving

  const save = (): void => {
    if (!canSave) return
    onSave(trimmed)
  }

  const cancel = (): void => {
    setDraft(name)
    setEditing(false)
  }

  if (!editing) {
    return (
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <p className="truncate" style={theme.textVariants.lead}>
          {name}
        </p>
        {canEdit && (
          <Button
            type="button"
            className="h-8 w-8"
            size="no-padding"
            iconSize={16}
            icon={IconChoices.EDIT}
            onClick={() => setEditing(true)}
            data-testid="room-settings-rename"
          />
        )}
      </div>
    )
  }

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-2">
      <TextInput
        label={translate('chatSettings.form.groupChatName')}
        value={draft}
        onChange={(e) => setDraft(e.currentTarget.value)}
        onBlur={() => undefined}
        onKeyUp={(e) => {
          if (e.key === 'Enter') save()
          if (e.key === 'Escape') cancel()
        }}
        disabled={isSaving}
      />
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          size="sm"
          label={translate('common.actions.cancel')}
          onClick={cancel}
          disabled={isSaving}
        />
        <Button
          type="button"
          size="sm"
          variant={ButtonVariant.PRIMARY}
          label={translate('common.actions.save')}
          onClick={save}
          disabled={!canSave}
          loading={isSaving}
          data-testid="room-settings-rename-save"
        />
      </div>
    </div>
  )
}
